import * as React from 'react'
import moment from 'moment'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../../../stores/CurrentCompanyStore'
import {
  parseAndDownloadCSV,
  GrPrice,
  orderedExportData,
  replaceArrayValue,
  bulkRenameObjectProperty,
} from '../../../../../../utilities/helpers'
import { ReportFilter } from '../../ReportFilter'
import { ReportFilterStore } from '../../ReportFilterStore'
import { Chart, Axis, Tooltip, Geom, Coord } from 'bizcharts'
import { VendorTopProductsModal } from './VendorTopProductsModal'
import { Skeleton } from 'antd'
import Helmet from 'react-helmet'
import { API } from '@getgreenline/homi-shared'
import DataSet from '@antv/data-set'
import {
  IReportV2Query,
  ReportFields,
  ReportDirection,
  IBaseReportData,
} from '@getgreenline/homi-shared/dist/models/Reports/ReportsV2'
import { Track } from '../../../../../../Track'
const { DataView } = DataSet

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  reportFilterStore?: ReportFilterStore
}

interface IOrder {
  field: ReportFields
  direction: ReportDirection
}

interface State {
  order: IOrder
  selectedSupplierId?: number
  results?: IBaseReportData[]
  isLoading: boolean
}

@inject('currentCompanyStore', 'reportFilterStore')
@observer
export class VendorsReport extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      order: {
        field: 'Net sales',
        direction: 'desc',
      },
      results: undefined,
      isLoading: false,
    }
  }

  componentDidMount = async () => {
    await this.getResults()
  }

  get fields() {
    const fields: ReportFields[] = [
      'Brand/Vendor',
      'Gross sales',
      'Refunds',
      'Discounts',
      'Net sales',
      'Cost',
      'Profit',
      'Margin',
      'Quantity',
    ]

    return fields
  }

  get query() {
    const { selectedLocationIds, startDateString, endDateString } = this.props.reportFilterStore!

    const query: IReportV2Query = {
      locationIds: selectedLocationIds,
      startDate: startDateString,
      endDate: endDateString,
      groupBy: {
        time: null,
        dimensions: ['Vendor'],
      },
      orderBy: {
        ...this.state.order,
      },
      fields: replaceArrayValue(this.fields, 'Brand/Vendor', 'Vendor'),
    }

    return query
  }

  getResults = async () => {
    const { id } = this.props.currentCompanyStore!.company!
    const { startDate, endDate } = this.props.reportFilterStore!
    await this.setState({ isLoading: true, results: [] })

    try {
      Track.startTimer('dashboard:reports_vendors')
      const apiResults = await API.getSales(id, this.query)
      const results = bulkRenameObjectProperty(apiResults, 'Vendor', 'Brand/Vendor')
      Track.track('dashboard:reports_vendors', {
        numberOfDays: moment(endDate).diff(startDate, 'days'),
      })
      this.setState({ results, isLoading: false })
    } catch (error) {
      this.setState({ results: undefined, isLoading: false })
    }
  }

  toggleOrder = async (fieldName: ReportFields) => {
    if (this.state.order.field === fieldName) {
      const currentDirection = this.state.order.direction
      this.setState({
        order: {
          field: fieldName,
          direction: currentDirection === 'asc' ? 'desc' : 'asc',
        },
      })
    } else {
      this.setState({
        order: {
          field: fieldName,
          direction: 'desc',
        },
      })
    }

    await this.getResults()
  }

  chartData = () => {
    const dv = new DataView()
    dv.source(this.state.results || []).transform({
      type: 'percent',
      field: 'Net sales',
      dimension: 'Brand/Vendor',
      as: 'percent',
    })
    return dv
  }

  tableRenderer = (rawData?: IBaseReportData[]) => {
    if (!rawData) {
      return <div>Error</div>
    }

    if (rawData.length > 0) {
      return (
        <>
          <Chart height={300} data={this.chartData()} renderer='svg' forceFit>
            <Coord type='theta' radius={0.75} />
            <Axis name='Net sales' />
            <Geom type='intervalStack' position='Net sales' color='Brand/Vendor'>
              <Tooltip />
            </Geom>
          </Chart>
          <table className='table table-sm'>
            <thead>
              <tr>
                <th>
                  <a onClick={() => this.toggleOrder('Vendor')}>Brand/Vendor</a>
                </th>
                <th className='text-right'>Products</th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Gross sales')}>Gross</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Refunds')}>Refunds</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Discounts')}>Discounts</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Net sales')}>Net</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Cost')}>Cost</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Profit')}>Profit</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Margin')}>Margin</a>
                </th>
                <th className='text-right'>
                  <a onClick={() => this.toggleOrder('Quantity')}>Qty</a>
                </th>
              </tr>
            </thead>
            <tbody>
              {rawData.map((data: any, index: number) => {
                return (
                  <tr key={index}>
                    <td>{data['Brand/Vendor'] || '-'}</td>
                    {data['Brand/Vendor'] ? (
                      <td className='text-right'>
                        <a onClick={() => this.setState({ selectedSupplierId: data['Vendor ID'] })}>
                          View
                        </a>
                      </td>
                    ) : (
                      <td className='text-right'>-</td>
                    )}
                    <td className='text-right'>
                      {GrPrice.convertDollarToDollar(data['Gross sales'])}
                    </td>
                    <td className='text-right'>{GrPrice.convertDollarToDollar(data['Refunds'])}</td>
                    <td className='text-right'>
                      {GrPrice.convertDollarToDollar(data['Discounts'])}
                    </td>
                    <td className='text-right'>
                      {GrPrice.convertDollarToDollar(data['Net sales'])}
                    </td>
                    <td className='text-right'>{GrPrice.convertDollarToDollar(data['Cost'])}</td>
                    <td className='text-right'>{GrPrice.convertDollarToDollar(data['Profit'])}</td>
                    <td className='text-right'>{data['Margin']}%</td>
                    <td className='text-right'>{data['Quantity']}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )
    } else {
      return <div>No data</div>
    }
  }

  export() {
    const { startDate, endDate } = this.props.reportFilterStore!
    const fields = this.fields
    const results = this.state.results
    const data = orderedExportData(fields, results)

    if (data) {
      parseAndDownloadCSV(
        `brand-report-${moment(startDate).format('YYYY-MM-DD')}-${moment(endDate).format(
          'YYYY-MM-DD',
        )}`,
        data,
      )
    }
  }

  render() {
    const currentCompanyStore = this.props.currentCompanyStore!
    const company = currentCompanyStore.company!

    return (
      <>
        <Helmet>
          <title>Reports | Brands/Vendors</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        <ReportFilter maxRangeMonths={12} />

        <div>
          <div className='rounded background-white'>
            <div className='card-body table-responsive'>
              <div>
                <b>Brand/Vendor sales</b>
              </div>
              <div className='text-muted'>Aggregated</div>

              <div className='mt-2 mb-2'>
                <button className='btn btn-light btn-sm' onClick={() => this.export()}>
                  Export as csv
                </button>
              </div>

              {this.state.isLoading ? <Skeleton active /> : this.tableRenderer(this.state.results)}
            </div>
          </div>
        </div>

        {!!this.state.selectedSupplierId && (
          <VendorTopProductsModal
            companyId={company.id}
            supplierId={this.state.selectedSupplierId}
            reportFilterStore={this.props.reportFilterStore!}
            onClose={() => this.setState({ selectedSupplierId: undefined })}
          />
        )}
      </>
    )
  }
}
